import { TEvent, TActExt } from '@ws/shared/types';
import { getDeepCopy } from '@ws/shared/utils';
import { TextMerger } from './TextMerger';
import {
  NOTE_UPDATE_CONTENT,
  NODE_PARENT_UPDATE,
  NODE_META_UPDATE,
  PROJECT_META_UPDATE,
  NOTE_CREATE,
  COLLECTION_CREATE,
  COLLECTION_UPDATE_CHILD,
  NODE_REMOVE,
} from '../events/act.types';

export type TConflictType = 'STRUCTURAL' | 'TEXT' | 'META' | 'NONE';

export interface IConflictResolution {
  isConflict: boolean;
  conflictType: TConflictType;
  resolvedEvent: TEvent | null;
}

export class ConflictManager {
  private textMerger: TextMerger;

  constructor() {
    this.textMerger = new TextMerger();
  }

  public resolve(
    local: TEvent,
    remote: TEvent,
    context: { baseContent?: string } = {},
  ): IConflictResolution {
    const localEntityId = this.getEntityIdFromEvent(local);
    const remoteEntityId = this.getEntityIdFromEvent(remote);

    // If different entities, no conflict at this level
    if (!localEntityId || !remoteEntityId || localEntityId !== remoteEntityId) {
      return { isConflict: false, conflictType: 'NONE', resolvedEvent: null };
    }

    // Same entity, check act types
    const localActType = local.acts[0].type;
    const remoteActType = remote.acts[0].type;

    // Both are text updates
    if (localActType === NOTE_UPDATE_CONTENT && remoteActType === NOTE_UPDATE_CONTENT) {
      if (context.baseContent) {
        const merged = this.textMerger.merge(
          context.baseContent,
          local.acts[0].payload.content,
          remote.acts[0].payload.content,
        );
        const resolvedEvent = getDeepCopy(remote);
        resolvedEvent.acts[0].payload.content = merged.text;
        // Even if we merged, we might want to flag it if it wasn't a "clean" merge
        return { isConflict: merged.hasConflict, conflictType: 'TEXT', resolvedEvent };
      }

      // Fallback: Last Write Wins but flag as conflict so UI can show it
      const resolvedEvent = Number(local.time) > Number(remote.time) ? local : remote;
      return { isConflict: true, conflictType: 'TEXT', resolvedEvent: getDeepCopy(resolvedEvent) };
    }

    // Structural conflicts
    if (localActType === NODE_PARENT_UPDATE && remoteActType === NODE_PARENT_UPDATE) {
      // If parent IDs are different, it's a structural conflict
      if (local.acts[0].payload.parentId !== remote.acts[0].payload.parentId) {
        return { isConflict: true, conflictType: 'STRUCTURAL', resolvedEvent: null };
      }
    }

    // Meta updates - usually safe to LWW without flagging
    if (localActType === NODE_META_UPDATE && remoteActType === NODE_META_UPDATE) {
      const resolvedEvent = Number(local.time) > Number(remote.time) ? local : remote;
      return { isConflict: false, conflictType: 'META', resolvedEvent: getDeepCopy(resolvedEvent) };
    }

    // If one is remove and other is update -> Conflict
    if (localActType === NODE_REMOVE || remoteActType === NODE_REMOVE) {
      return { isConflict: true, conflictType: 'STRUCTURAL', resolvedEvent: null };
    }

    // Default LWW
    const resolvedEvent = Number(local.time) > Number(remote.time) ? local : remote;
    return { isConflict: false, conflictType: 'NONE', resolvedEvent: getDeepCopy(resolvedEvent) };
  }

  private getEntityIdFromEvent(event: TEvent): string | null {
    if (!event.acts.length) return null;
    return this.getEntityId(event.acts[0]);
  }

  private getEntityId(act: TActExt): string | null {
    switch (act.type) {
      case PROJECT_META_UPDATE:
        return act.payload.projectId;
      case NOTE_CREATE:
        return act.payload.note.id;
      case COLLECTION_CREATE:
        return act.payload.collection.id;
      case NODE_META_UPDATE:
        return act.payload.ref.id;
      case NODE_PARENT_UPDATE:
        return act.payload.ref.id;
      case NOTE_UPDATE_CONTENT:
        return act.payload.entityId;
      case COLLECTION_UPDATE_CHILD:
        return act.payload.entityId;
      case NODE_REMOVE:
        return act.payload.ref.id;
      default:
        return null;
    }
  }
}
